import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setSelectedCustomer,
  setSelectedProduct,
  setQuantity,
  setPrice,
  setVat,
  setCustomText,
} from "../../../app/store/store";
import jsPDF from "jspdf";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import AsyncSelect from "react-select/async";
import {
  getOutstandingInvoicesByCustomer,
  addInvoice,
} from "../services/invoiceService";
import { searchProducts } from "../../product/services/productService";
import { getCompanyInfo } from "../../../services/authService";
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip,
  Paper,
  Grid,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  LinearProgress,
  Stack,
  useTheme,
  useMediaQuery,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  CircularProgress, // ✅ Add this import
} from "@mui/material";
import { toast } from "react-toastify";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import PreviewIcon from "@mui/icons-material/Preview";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import PaymentIcon from "@mui/icons-material/Payment";
import DiscountIcon from "@mui/icons-material/Discount";
import NotesIcon from "@mui/icons-material/Notes";
import CalculateIcon from "@mui/icons-material/Calculate";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InvoicePreviewDialog from "../../../components/InvoicePreviewDialog";
import CustomerSelect from "../components/CustomerSelect";
import ProductSelect from "../components/ProductSelect";
import OutstandingInvoicesSection from "../../../components/OutstandingInvoicesSection";
import PaymentSection from "../components/PaymentSection";
import { searchCustomers } from "../../customers/services/customerService";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import html2canvas from 'html2canvas';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';

const vatOptions = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 21, label: "21%" },
];

const MAX_CUSTOM_TEXT_LENGTH = 300;

function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function AddInvoicePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate(); // ✅ Add this hook
  const customers = useSelector((state) => state.customer.customers) || [];
  const products = useSelector((state) => state.product.products) || [];
  const {
    selectedCustomer,
    selectedProduct,
    quantity,
    price,
    vat,
    customText,
  } = useSelector((state) => state.invoice);
  
  // ✅ Move ALL useState declarations to the TOP, before any conditional returns
  const [templateHtml, setTemplateHtml] = useState("");
  const [invoiceProducts, setInvoiceProducts] = useState([]);
  const [editingRowKey, setEditingRowKey] = useState(null);
  const [editFields, setEditFields] = useState({
    Quantity: 1,
    Price: "",
    VAT: 0,
  });

  const [invoicePrice, setInvoicePrice] = useState("");
  const [invoicePaidAmount, setInvoicePaidAmount] = useState("");
  const [invoiceDueAmount, setInvoiceDueAmount] = useState("");

  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  const [invoiceNumber, setInvoiceNumber] = useState(
    generateInvoiceNumber(Date.now() % 10000)
  );

  const [companyInfo, setCompanyInfo] = useState({
    name: "",
    vat: "",
    email: "",
    iban: "",
  });

  const [invoiceDate, setInvoiceDate] = useState(new Date());

  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 15);
    return d;
  });

  const [previewHtml, setPreviewHtml] = useState("");
  const [openPreview, setOpenPreview] = useState(false);

  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(0);

  const [showOutstanding, setShowOutstanding] = useState(true);

  // ✅ Add isSendOnPeppol state HERE, before any conditional returns
  const [isSendOnPeppol, setIsSendOnPeppol] = useState(false);

  // ✅ Add loading state for initial data fetch
  const [dataLoading, setDataLoading] = useState(true);

  // ✅ Add invoice-level VAT state (after discountValue state, around line 154)
  const [invoiceVAT, setInvoiceVAT] = useState(21); // Default 21%

  // Add validation state after other useState declarations (around line 154)
  const [paidAmountError, setPaidAmountError] = useState(false);

  const location = useLocation();
  const { t, i18n } = useTranslation();
  const language =
    useSelector((state) => state.settings?.language) || i18n.language || "nl";

  // Debounce ref
  const debounceRef = useRef();

  // ✅ Now all useEffect hooks come after ALL useState declarations
  useEffect(() => {
    const fetchInitialData = async () => {
      setDataLoading(true);
      
      try {
        // ✅ Fetch company info with error handling
        try {
          const data = await getCompanyInfo();
          if (data) {
            setCompanyInfo({
              name: data.CompanyName || "",
              vat: data.BTWNumber || "",
              email: data.Email || "",
              iban: data.IBANumber || "",
            });
            console.log('✅ Company info loaded');
          } else {
            throw new Error('No company data received');
          }
        } catch (companyError) {
          console.error('❌ Error fetching company info:', companyError);
          setCompanyInfo({
            name: "",
            vat: "",
            email: "",
            iban: "",
          });
          toast.warning(t("failed_to_load_company_info"));
        }

        // ✅ Fetch template with error handling
        let templateFile = "template-nl.html";
        if (language === "fr") templateFile = "template-fr.html";
        else if (language === "en") templateFile = "template-en.html";

        try {
          const response = await fetch(`/invoicetemplates/${templateFile}`);
          if (response.ok) {
            const html = await response.text();
            setTemplateHtml(html);
            console.log('✅ Template loaded');
          } else {
            throw new Error(`Failed to load template: ${response.status}`);
          }
        } catch (templateError) {
          console.error('❌ Error fetching template:', templateError);
          setTemplateHtml("");
          toast.warning(t("failed_to_load_template"));
        }
        
      } catch (error) {
        console.error('❌ Critical error during initial data fetch:', error);
        toast.error(t("failed_to_load_initial_data"));
      } finally {
        setDataLoading(false);
      }
    };

    fetchInitialData();
  }, [language, t]);

  useEffect(() => {
    const price = parseFloat(invoicePrice) || 0;
    const paid = parseFloat(invoicePaidAmount) || 0;
    setInvoiceDueAmount((price - paid >= 0 ? price - paid : 0).toFixed(2));
  }, [invoicePrice, invoicePaidAmount]);

  useEffect(() => {
    if (selectedCustomer?.Id) {
      fetchOutstandingInvoices(selectedCustomer.Id);
    } else {
      setOutstandingInvoices([]);
      setSelectedInvoices([]);
    }
  }, [selectedCustomer]);

  // ✅ Add useEffect to reset Peppol toggle when customer changes
  useEffect(() => {
    // Reset Peppol toggle when customer changes
    setIsSendOnPeppol(false);
  }, [selectedCustomer]);

  useEffect(() => {
    dispatch(setSelectedCustomer(null));
    setOutstandingInvoices([]);
    setSelectedInvoices([]);
    setInvoiceProducts([]);
    setSelectedProduct(null);
    setQuantity(1);
    setPrice("");
    setVat(0);
    setCustomText("");
    setInvoicePaidAmount(0);
    setEditingRowKey(null);
    setEditFields({});
  }, []);

  // ✅ Enhanced fetch outstanding invoices with error handling
  const fetchOutstandingInvoices = async (customerId) => {
    try {
      const data = await getOutstandingInvoicesByCustomer(customerId);
      
      // ✅ Safely handle response
      if (Array.isArray(data)) {
        setOutstandingInvoices(data);
        console.log('✅ Outstanding invoices loaded:', data.length);
      } else if (data?.payload && Array.isArray(data.payload)) {
        setOutstandingInvoices(data.payload);
        console.log('✅ Outstanding invoices loaded:', data.payload.length);
      } else {
        setOutstandingInvoices([]);
      }
    } catch (error) {
      console.error('❌ Error fetching outstanding invoices:', error);
      setOutstandingInvoices([]);
      
      if (error.response?.status === 404) {
        // No outstanding invoices - not an error
        console.log('ℹ️ No outstanding invoices for this customer');
      } else if (error.response?.status === 401) {
        toast.error(t("session_expired"));
      } else if (error.code === 'ERR_NETWORK') {
        toast.error(t("network_error"));
      } else {
        toast.warning(t("failed_to_load_outstanding_invoices"));
      }
    }
  };

  const handleInvoiceToggle = (invoice) => {
    setSelectedInvoices((prev) =>
      prev.some((i) => i.Id === invoice.Id)
        ? prev.filter((i) => i.Id !== invoice.Id)
        : [...prev, invoice]
    );
  };

  const handleAddProduct = () => {
    if (selectedProduct && quantity > 0) {
      setInvoiceProducts([
        ...invoiceProducts,
        {
          ...selectedProduct,
          Quantity: quantity,
          Price: price,
          TotalExcl: (quantity * price).toFixed(2),
          _rowKey: Date.now() + Math.random(),
        },
      ]);
      dispatch(setSelectedProduct(null));
      dispatch(setQuantity(1));
      dispatch(setPrice(""));
      dispatch(setVat(0)); // Keep this for Redux state cleanup
    }
  };

  const handleRemoveProduct = (rowKey) => {
    setInvoiceProducts(invoiceProducts.filter((p) => p._rowKey !== rowKey));
  };

  const handleGeneratePDF = async () => {
    try {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = getFilledTemplate();
      
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      tempDiv.style.width = "794px";
      tempDiv.style.background = "white";
      document.body.appendChild(tempDiv);

      const images = tempDiv.getElementsByTagName('img');
      const imagePromises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          const timeout = setTimeout(() => resolve(), 3000);
          img.onload = () => {
            clearTimeout(timeout);
            resolve();
          };
          img.onerror = () => {
            clearTimeout(timeout);
            resolve();
          };
        });
      });

      await Promise.all(imagePromises);

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        width: 794,
        windowWidth: 794,
        backgroundColor: '#ffffff',
        onclone: (clonedDoc) => {
          clonedDoc.body.style.background = 'white';
        }
      });

      document.body.removeChild(tempDiv);

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('Invalid canvas generated');
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      if (imgHeight > pdfHeight) {
        const scaledWidth = (canvas.width * pdfHeight) / canvas.height;
        const scaledHeight = pdfHeight;
        pdf.addImage(imgData, 'PNG', 0, 0, scaledWidth, scaledHeight);
      } else {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      }

      pdf.save(`invoice-${invoiceNumber}.pdf`);
      
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      
      if (error.message.includes('Invalid canvas')) {
        toast.error(t("failed_to_render_invoice"));
      } else if (error.message.includes('html2canvas')) {
        toast.error(t("failed_to_convert_html"));
      } else {
        toast.error(t("failed_to_generate_pdf"));
      }
    }
  };

  function formatDate(date) {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const totalExcl =
    invoiceProducts.reduce((sum, p) => sum + Number(p.TotalExcl), 0) +
    selectedInvoices.reduce(
      (sum, inv) => sum + Number(inv.InvoiceDueAmount),
      0
    );

  const totalVAT = (totalExcl * invoiceVAT) / 100;

  const totalAmount = totalExcl + totalVAT;

  let discountAmount = 0;
  if (discountType === "percentage") {
    discountAmount = (totalAmount * (parseFloat(discountValue) || 0)) / 100;
  } else {
    discountAmount = parseFloat(discountValue) || 0;
  }
  const grandTotal = totalAmount - discountAmount;

  const customTextSection = stripHtml(customText)
    ? `<div style="margin: 24px 0 16px 0; padding: 12px 16px; background: #fffbe6; border: 1px solid #ffe082; border-radius: 6px;">
      <span style="font-weight: bold; color: #d32f2f; font-size: 1.1em; margin-right: 8px;">${t("notes")}</span>
      <span style="font-weight: 600; color: #795548;">${stripHtml(
        customText
      )}</span>
    </div>`
    : "";

  const getFilledTemplate = () => {
    // ✅ Calculate total quantity
    const totalQuantity = invoiceProducts.reduce((sum, p) => sum + Number(p.Quantity), 0);

    // ✅ Calculate payment status
    const paid = parseFloat(invoicePaidAmount) || 0;
    const total = grandTotal;
    let paymentStatus = t("unpaid");
    if (paid >= total) paymentStatus = t("paid");
    else if (paid > 0 && paid < total) paymentStatus = t("partially_paid");

    // ✅ Update VAT Section to use invoice-level VAT
    const VATSection = invoiceVAT > 0
      ? `
      <div>
        <span>${invoiceVAT}% ${t("vat")} on €${totalExcl.toFixed(2)}:</span>
        <span>€${totalVAT.toFixed(2)}</span>
      </div>
    `
      : `<div><span>0% ${t("vat")}:</span><span>€0.00</span></div>`;

    // ✅ Build discount row (if applicable)
    const DiscountRow = discountAmount > 0
      ? `
    <tr class="discount-row">
      <td colspan="5" style="text-align:right; font-style: italic;">
        ${discountType === "percentage" ? `${discountValue}%` : `€${discountValue}`} ${t("discount")}:
      </td>
      <td class="text-right">- €${discountAmount.toFixed(2)}</td>
    </tr>
    `
      : '';

    // ✅ Update product rows to remove VAT column
    const ProductRows = invoiceProducts.map(p => `
    <tr>
      <td>${p.SKU || p.Id}</td>
      <td class="text-center">${p.Quantity}</td>
      <td>${p.Name}</td>
      <td class="text-right">€${parseFloat(p.Price).toFixed(2)}</td>
      <td class="text-right">€${parseFloat(p.Price).toFixed(2)}</td>
      <td class="text-right">€${parseFloat(p.TotalExcl).toFixed(2)}</td>
    </tr>
  `).join('');

    // ✅ Replace all placeholders
    let filledTemplate = templateHtml
      // Company Info
      .replace(/{{CompanyName}}/g, companyInfo.name || "N/A")
      .replace(/{{CompanyVAT}}/g, companyInfo.vat || "N/A")
      .replace(/{{CompanyEmail}}/g, companyInfo.email || "N/A")
      .replace(/{{CompanyIBAN}}/g, companyInfo.iban || "N/A")
      .replace(/{{CompanyBankDetail}}/g, companyInfo.iban || "N/A")
      .replace(/{{CompanyAddress}}/g, "")
      .replace(/{{CompanyPhone}}/g, "")
      .replace(/{{CompanyWebsite}}/g, "")
      
      // Customer Info
      .replace(/{{CustomerName}}/g, selectedCustomer?.Name || "N/A")
      .replace(/{{CustomerId}}/g, selectedCustomer?.Id || "")
      .replace(/{{CustomerVAT}}/g, selectedCustomer?.VATNumber || "N/A")
      .replace(/{{CustomerNumber}}/g, selectedCustomer?.Id || "")
      .replace(/{{CustomerAddress}}/g, `${selectedCustomer?.Street || ""} ${selectedCustomer?.Box || ""}`.trim() || "N/A")
      .replace(/{{CustomerCity}}/g, `${selectedCustomer?.PostCode || ""} ${selectedCustomer?.City || ""}`.trim() || "N/A")
      
      // Invoice Info
      .replace(/{{InvoiceNumber}}/g, invoiceNumber)
      .replace(/{{InvoiceDate}}/g, formatDate(invoiceDate))
      .replace(/{{DueDate}}/g, formatDate(dueDate))
      
      // Product Info
      .replace(/{{ProductName}}/g, selectedProduct?.Name || "")
      .replace(/{{ProductId}}/g, selectedProduct?.Id || "")
      .replace(/{{Quantity}}/g, quantity.toString())
      .replace(/{{Price}}/g, price.toString())
      .replace(/{{VAT}}/g, invoiceVAT.toString()) // ✅ Use invoice-level VAT
      
      // Product Rows and Totals
      .replace(/{{ProductRows}}/g, ProductRows)
      .replace(/{{TotalQuantity}}/g, totalQuantity.toString())
      .replace(/{{DiscountRow}}/g, DiscountRow)
      
      // Financial Totals
      .replace(/{{TotalExcl}}/g, totalExcl.toFixed(2))
      .replace(/{{TotalVAT}}/g, totalVAT.toFixed(2))
      .replace(/{{TotalAmount}}/g, totalAmount.toFixed(2))
      .replace(/{{TotalIncl}}/g, totalIncl.toFixed(2))
      .replace(/{{GrandTotal}}/g, grandTotal.toFixed(2))
      .replace(/{{InvoicePaidAmount}}/g, (parseFloat(invoicePaidAmount) || 0).toFixed(2))
      .replace(/{{InvoiceDueAmount}}/g, (grandTotal - (parseFloat(invoicePaidAmount) || 0)).toFixed(2))
      
      // VAT Section
      .replace(/{{VATSection}}/g, VATSection)
      
      // Payment Status
      .replace(/{{PaymentStatus}}/g, paymentStatus)
      
      // Outstanding Invoices
      .replace(/{{OutstandingRows}}/g, outstandingRows)
      .replace(/{{OutstandingSection}}/g, outstandingSection)
      
      // Custom Text
      .replace(/{{CustomText}}/g, customText)
      .replace(/{{CustomTextSection}}/g, customTextSection)
      
      // Page Numbers (if applicable)
      .replace(/{{PageNumber}}/g, "1")
      .replace(/{{TotalPages}}/g, "1");

    return filledTemplate;
  };

  const productRows = invoiceProducts
    .map(
      (p) => `
  <tr>
    <td>${p.Name}</td>
    <td>${p.Quantity}</td>
    <td>€${p.Price}</td>
    <td>${p.VAT}%</td>
    <td>€${p.TotalExcl}</td>
  </tr>
`
    )
    .join("");

  const outstandingRows = selectedInvoices
    .map(
      (inv) => `
  <tr>
    <td>${inv.InvoiceNumber}</td>
    <td>${formatDate(inv.CreatedDate)}</td>
    <td>€${inv.InvoiceDueAmount}</td>
  </tr>
`
    )
    .join("");

  const outstandingSection =
    selectedInvoices.length > 0
      ? `
      <h4 style="margin-top:24px;">${t("outstanding_invoices")}</h4>
      <table style="width:100%; border-collapse:collapse; margin-top:12px; font-size:0.85em;">
        <thead>
          <tr style="background:#f6f6f6;">
            <th style="border:1px solid #ccc; padding:8px;">${t("invoice_number")}</th>
            <th style="border:1px solid #ccc; padding:8px;">${t("invoice_date")}</th>
            <th style="border:1px solid #ccc; padding:8px;">${t("total_amount")}</th>
          </tr>
        </thead>
        <tbody>
          ${outstandingRows}
        </tbody>
      </table>
    `
      : "";

  const totalIncl = totalExcl + totalVAT - discountAmount;

  useEffect(() => {
    const paid = parseFloat(invoicePaidAmount) || 0;
    setInvoiceDueAmount((grandTotal - paid >= 0 ? grandTotal - paid : 0).toFixed(2));
  }, [grandTotal, invoicePaidAmount, discountType, discountValue]);

  const customTextLength = stripHtml(customText).length;

  const handleCustomTextChange = (value) => {
    const plainText = stripHtml(value);
    if (plainText.length <= MAX_CUSTOM_TEXT_LENGTH) {
      dispatch(setCustomText(value));
    } else {
      const allowedText = plainText.slice(0, MAX_CUSTOM_TEXT_LENGTH);
      dispatch(setCustomText(allowedText));
    }
  };

  const loadCustomerOptions = async (inputValue) => {
    try {
      const data = await searchCustomers(inputValue);
      
      const customersArray = Array.isArray(data?.payload)
        ? data.payload
        : Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : [];

      return customersArray.map((c) => ({
        ...c,
        value: c.Id,
        label: `${c.Name} (${c.Email || 'No email'})`,
      }));
    } catch (error) {
      console.error('❌ Error searching customers:', error);
      
      if (error.response?.status === 401) {
        toast.error(t("session_expired"));
      } else if (error.code === 'ERR_NETWORK') {
        toast.error(t("network_error"));
      }
      
      return [];
    }
  };

  const loadProductOptions = (inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!inputValue) return callback([]);

      try {
        const barcodeLengths = [8, 12, 13, 14];
        let paramsObj;

        if (barcodeLengths.includes(inputValue.length) && /^\d+$/.test(inputValue)) {
          paramsObj = { barCode: inputValue };
        } else if (/^\d+$/.test(inputValue)) {
          paramsObj = { sku: inputValue, id: inputValue };
        } else if (inputValue.length >= 3) {
          paramsObj = { name: inputValue };
        } else {
          return callback([]);
        }

        const data = await searchProducts(paramsObj);

        // ✅ Safely handle undefined or missing payload
        const productsArray = Array.isArray(data?.payload)
          ? data.payload
          : Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];

        callback(
          productsArray.map((p) => ({
            value: p.Id,
            label: `${p.Name} (ID: ${p.Id})${p.SKU ? " - SKU: " + p.SKU : ""}${p.BarCode ? " - BarCode: " + p.BarCode : ""}`,
            ...p,
          }))
        );
      } catch (error) {
        console.error('❌ Error searching products:', error);
        
        if (error.response?.status === 401) {
          toast.error(t("session_expired"));
        } else if (error.code === 'ERR_NETWORK') {
          toast.error(t("network_error"));
        }
        
        callback([]);
      }
    }, 350);
  };

  const handleAddInvoiceAndGeneratePDF = async () => {
    // Validate paid amount
    if (!invoicePaidAmount || invoicePaidAmount === "" || parseFloat(invoicePaidAmount) < 0) {
      setPaidAmountError(true);
      return;
    }

    // Reset error if validation passes
    setPaidAmountError(false);

    try {
      let status = t("unpaid");
      const paid = parseFloat(invoicePaidAmount) || 0;
      const total = totalIncl;
      if (paid >= total) status = t("paid");
      else if (paid > 0 && paid < total) status = t("partially_paid");

      const invoiceData = {
        invoiceNumber: invoiceNumber,
        invoicePrice: total,
        invoicePaidAmount: paid,
        invoiceDueAmount: Math.max(total - paid, 0),
        invoiceUrl: "test",
        customerId: selectedCustomer?.Id || selectedCustomer?.value,
        customText: stripHtml(customText),
        Status: status,
        VATPercentage: invoiceVAT, // ✅ Changed from VAT to VATPercentage
        IsSendOnPeppol: isSendOnPeppol,
        LineItems: invoiceProducts.map((p) => ({
          productId: p.Id,
          quantity: p.Quantity,
          price: p.Price,
          totalExcl: p.TotalExcl,
        })),
        OutstandingInvoices: selectedInvoices.map((inv) => ({
          invoiceId: inv.Id,
          invoiceNumber: inv.InvoiceNumber,
          dueAmount: inv.InvoiceDueAmount,
        })),
      };

      const response = await addInvoice(invoiceData);

      if (response) {
        toast.success(t("invoice_created_success"));
        await handleGeneratePDF();
        resetInvoiceForm();
        setPaidAmountError(false);
        
        // ✅ Redirect to list invoices page after successful creation
        setTimeout(() => {
          navigate('/invoices'); // or '/invoices/list' depending on your route
        }, 1500); // 1.5 second delay to show success message and allow PDF download
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('❌ Error creating invoice:', error);
      
      if (error.response?.status === 400) {
        toast.error(t("invalid_invoice_data"));
      } else if (error.response?.status === 401) {
        toast.error(t("session_expired"));
      } else if (error.response?.status === 409) {
        toast.error(t("invoice_already_exists"));
      } else if (error.code === 'ERR_NETWORK') {
        toast.error(t("network_error"));
      } else {
        toast.error(error.response?.data?.message || t("invoice_created_error"));
      }
    }
  };

  // Add a function to reset the form (if not already exists)
  const resetInvoiceForm = () => {
    dispatch(setSelectedCustomer(null));
    dispatch(setSelectedProduct(null));
    dispatch(setQuantity(1));
    dispatch(setPrice(""));
    dispatch(setVat(0));
    dispatch(setCustomText(""));
    setInvoiceProducts([]);
    setSelectedInvoices([]);
    setInvoicePaidAmount("");
    setDiscountValue(0);
    setInvoiceVAT(21);
    setIsSendOnPeppol(false);
    setPaidAmountError(false); // Reset error
    setInvoiceNumber(generateInvoiceNumber(Date.now() % 10000));
  };

  // ✅ Show loading screen while fetching initial data
  if (dataLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 400,
          gap: 2
        }}>
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            {t("loading_invoice_data")}...
          </Typography>
        </Box>
      </Container>
    );
  }

  // ✅ Show warning if template failed to load
  if (!templateHtml) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper 
          elevation={3}
          sx={{ 
            p: 4,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <ReceiptLongIcon sx={{ fontSize: 60, color: 'error.main' }} />
            <Typography variant="h6" color="error" textAlign="center">
              {t("failed_to_load_template")}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t("please_refresh_or_contact_support")}
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              startIcon={<RefreshIcon />}
            >
              {t("refresh_page")}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{
      background: theme.palette.mode === 'dark'
        ? 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 100%)'
        : 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)',
      minHeight: '100vh',
      pt: 3,
      pb: 8
    }}>
      <Container maxWidth="lg">
        <Card
          elevation={3}
          sx={{
            borderRadius: '16px',
            overflow: 'hidden',
            mb: 4
          }}
        >
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  p: 1.2,
                  borderRadius: '12px',
                  mr: 2
                }}>
                  <ReceiptLongIcon color="primary" fontSize="large" />
                </Box>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {t("invoice_details")}
                </Typography>
                <Chip
                  label={invoiceNumber}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "bold",
                    height: 32,
                    px: 2,
                    borderRadius: '8px',
                    background: alpha(theme.palette.primary.main, 0.08),
                    ml: 2
                  }}
                />
              </Box>
            }
            sx={{
              background: alpha(theme.palette.primary.light, 0.1),
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
            }}
          />

          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                  {/* Customer Section */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} mb={1} justifyContent="space-between">
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Box sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                          p: 1,
                          borderRadius: '10px'
                        }}>
                          <PersonIcon color="primary" />
                        </Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ ml: 2 }}>
                          {t("customer")}
                        </Typography>
                      </Box>
                      {/* Right aligned toggle for outstanding invoices */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                          checked={showOutstanding}
                          onChange={() => setShowOutstanding((prev) => !prev)}
                          color="primary"
                        />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {t("show_outstanding_invoices")}
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <CustomerSelect
                        value={selectedCustomer}
                        onChange={(selectedOption) => {
                          dispatch(setSelectedCustomer(selectedOption));
                        }}
                        loadOptions={loadCustomerOptions}
                      />
                    </Box>
                  </Paper>

                  {/* Product Section */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                      <Box sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        p: 1,
                        borderRadius: '10px'
                      }}>
                        <InventoryIcon color="primary" />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {t("product")}
                      </Typography>
                    </Stack>
                    <ProductSelect
                      value={selectedProduct}
                      onChange={(option) => {
                        dispatch(setSelectedProduct(option));
                        dispatch(setPrice(option?.SalePrice || ""));
                      }}
                      loadOptions={loadProductOptions}
                      quantity={quantity}
                      setQuantity={(q) => dispatch(setQuantity(q))}
                      price={price}
                      setPrice={(p) => dispatch(setPrice(p))}
                      vat={null} // ✅ Remove VAT from product selection
                      setVat={null} // ✅ Remove VAT from product selection
                      vatOptions={[]} // ✅ Remove VAT options
                      onAdd={handleAddProduct}
                      disabled={!selectedProduct || quantity <= 0 || !price || price <= 0}
                    />
                  </Paper>

                  {/* Product List */}
                  {invoiceProducts.length > 0 && (
                    <Paper
                      elevation={1}
                      sx={{
                        borderRadius: '12px',
                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        bgcolor: alpha(theme.palette.success.main, 0.05),
                        overflow: 'hidden'
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} p={2}>
                        <Box sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.15),
                          p: 1,
                          borderRadius: '10px'
                        }}>
                          <InventoryIcon color="success" />
                        </Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="success.dark">
                          {t("products_to_be_invoiced")}
                        </Typography>
                        <Chip
                          label={`${invoiceProducts.length} ${t("items")}`}
                          color="success"
                          size="small"
                          sx={{ borderRadius: '8px', fontWeight: 'bold' }}
                        />
                      </Stack>
                      <Box sx={{ overflowX: 'auto', width: '100%' }}>
                        <Table size="small" sx={{ minWidth: 650 }}>
                          <TableHead>
                            <TableRow sx={{
                              background: alpha(theme.palette.success.main, 0.2)
                            }}>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                {t("product_name")}
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                {t("quantity")}
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                {t("price")}
                              </TableCell>
                              {/* ✅ Removed VAT column */}
                              <TableCell sx={{ fontWeight: "bold" }}>
                                {t("total_excl")}
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                {t("actions")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {invoiceProducts.map((p) => (
                              <TableRow key={p._rowKey} hover>
                                <TableCell sx={{ py: 1 }}>
                                  <Typography fontWeight={500}>{p.Name}</Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Chip label={p.Quantity} size="small" color="info" sx={{ borderRadius: '8px' }} />
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Typography fontWeight={500}>€{p.Price}</Typography>
                                </TableCell>
                                {/* ✅ Removed VAT cell */}
                                <TableCell sx={{ py: 1 }}>
                                  <Typography fontWeight={600} color="success.main">
                                    €{p.TotalExcl}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <IconButton
                                    onClick={() => handleRemoveProduct(p._rowKey)}
                                    color="error"
                                    size="small"
                                    sx={{
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.error.main, 0.1),
                                        transform: 'scale(1.1)'
                                      }
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Paper>
                  )}

                  {/* Outstanding Invoices - moved under Product List */}
                  {selectedCustomer && outstandingInvoices.length > 0 && showOutstanding && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: '12px',
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        bgcolor: alpha(theme.palette.warning.main, 0.05)
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                        <Box sx={{
                          bgcolor: alpha(theme.palette.warning.main, 0.15),
                          p: 1,
                          borderRadius: '10px'
                        }}>
                          <ReceiptLongIcon color="warning" />
                        </Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="warning.dark">
                          {t("outstanding_invoices")}
                        </Typography>
                        <Chip
                          label={`${outstandingInvoices.length}`}
                          color="warning"
                          size="small"
                          sx={{ borderRadius: '8px', fontWeight: 'bold' }}
                        />
                      </Stack>
                      <Box sx={{ overflowX: 'auto', width: '100%' }}>
                        <Table size="small" sx={{ minWidth: 500 }}>
                          <TableHead>
                            <TableRow sx={{
                              background: alpha(theme.palette.warning.main, 0.2)
                            }}>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                {t("invoice_number")}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                {t("total_amount")}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                {t("add_to_invoice")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {outstandingInvoices.map((inv) => (
                              <TableRow key={inv.Id} hover>
                                <TableCell sx={{ py: 1 }}>
                                  <Typography fontWeight={500}>
                                    {inv.InvoiceNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1 }}>
                                  <Chip
                                    label={`€${inv.InvoiceDueAmount}`}
                                    color="warning"
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderRadius: '8px', fontWeight: 'medium' }}
                                  />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1 }}>
                                  <Checkbox
                                    checked={selectedInvoices.some((i) => i.Id === inv.Id)}
                                    onChange={() => handleInvoiceToggle(inv)}
                                    color="warning"
                                    sx={{
                                      '&.Mui-checked': {
                                        color: theme.palette.warning.main,
                                      }
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Paper>
                  )}

                  {/* Custom Text */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                      <Box sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        p: 1,
                        borderRadius: '10px'
                      }}>
                        <NotesIcon color="primary" />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {t("custom_text")}
                      </Typography>
                    </Stack>
                    <Box sx={{
                      '.ql-toolbar': {
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)} !important`,
                        borderBottom: 'none !important',
                        backgroundColor: alpha(theme.palette.background.paper, 0.7)
                      },
                      '.ql-container': {
                        borderBottomLeftRadius: '8px',
                        borderBottomRightRadius: '8px',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)} !important`,
                        backgroundColor: alpha(theme.palette.background.paper, 0.5),
                        minHeight: '100px'
                      }
                    }}>
                      <ReactQuill
                        value={customText}
                        onChange={handleCustomTextChange}
                        style={{ marginBottom: 16 }}
                      />
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(customTextLength / MAX_CUSTOM_TEXT_LENGTH) * 100}
                        sx={{
                          flexGrow: 1,
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha(theme.palette.grey[300], 0.5)
                        }}
                        color={customTextLength >= MAX_CUSTOM_TEXT_LENGTH ? "error" : "primary"}
                      />
                      <Typography
                        variant="caption"
                        color={customTextLength >= MAX_CUSTOM_TEXT_LENGTH ? "error" : "text.secondary"}
                        sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}
                      >
                        {customTextLength} / {MAX_CUSTOM_TEXT_LENGTH}
                      </Typography>
                    </Box>
                  </Paper>
                </Stack>
              </Grid>

              {/* Right Column: Payment & Discount + Action Buttons */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={3}
                  sx={{
                    borderRadius: '16px',
                    background: alpha(theme.palette.info.light, 0.05),
                    mb: 2
                  }}
                >
                  <CardHeader
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.15),
                          p: 1.2,
                          borderRadius: '12px',
                          mr: 2
                        }}>
                          <PaymentIcon color="info" fontSize="large" />
                        </Box>
                        <Typography variant="h6" fontWeight="bold" color="info.main">
                          {t("payment_and_discount")}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      background: alpha(theme.palette.info.light, 0.1),
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                    }}
                  />
                  <CardContent>
                    <Stack spacing={3}>
                      {/* Payment Section */}
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                          bgcolor: 'white'
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                          <Box sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.15),
                            p: 1,
                            borderRadius: '10px'
                          }}>
                            <PaymentIcon color="info" />
                          </Box>
                          <Typography variant="subtitle1" fontWeight="bold" color="info.dark">
                            {t("payment")}
                          </Typography>
                        </Stack>
                        <PaymentSection
                          totalIncl={totalIncl}
                          invoicePaidAmount={invoicePaidAmount}
                          setInvoicePaidAmount={setInvoicePaidAmount}
                          error={paidAmountError} // ✅ Pass error state
                          onFocus={() => setPaidAmountError(false)} // ✅ Clear error on focus
                        />
                      </Paper>

                      {/* Discount Section */}
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                          bgcolor: 'white'
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                          <Box sx={{
                            bgcolor: alpha(theme.palette.secondary.main, 0.15),
                            p: 1,
                            borderRadius: '10px'
                          }}>
                            <DiscountIcon color="secondary" />
                          </Box>
                          <Typography variant="subtitle1" fontWeight="bold" color="secondary.dark">
                            {t("discount")}
                          </Typography>
                        </Stack>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="medium">
                              <InputLabel id="discount-type-label">{t("type")}</InputLabel>
                              <Select
                                labelId="discount-type-label"
                                value={discountType}
                                label={t("type")}
                                onChange={(e) => setDiscountType(e.target.value)}
                                sx={{ borderRadius: '10px' }}
                              >
                                <MenuItem value="percentage">{t("percentage")}</MenuItem>
                                <MenuItem value="fixed">{t("fixed_amount")}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label={t("value")}
                              value={discountValue}
                              onChange={(e) => setDiscountValue(e.target.value)}
                              placeholder={discountType === "percentage" ? t("eg_10") : t("eg_50")}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '10px'
                                }
                              }}
                              InputProps={{
                                startAdornment: discountType === "percentage" ? null : <Box sx={{ mr: 1 }}>€</Box>,
                                endAdornment: discountType === "percentage" ? <Box sx={{ ml: 1 }}>%</Box> : null
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* Invoice-Level VAT Section - Add this entire block */}
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                          bgcolor: 'white'
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                          <Box sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.15),
                            p: 1,
                            borderRadius: '10px'
                          }}>
                            <CalculateIcon color="info" />
                          </Box>
                          <Typography variant="subtitle1" fontWeight="bold" color="info.dark">
                            {t("vat_rate")}
                          </Typography>
                        </Stack>
                        <FormControl fullWidth size="medium">
                          <InputLabel id="invoice-vat-label">{t("vat_percentage")}</InputLabel>
                          <Select
                            labelId="invoice-vat-label"
                            value={invoiceVAT}
                            label={t("vat_percentage")}
                            onChange={(e) => setInvoiceVAT(e.target.value)}
                            sx={{ borderRadius: '10px' }}
                          >
                            <MenuItem value={0}>0%</MenuItem>
                            <MenuItem value={6}>6%</MenuItem>
                            <MenuItem value={12}>12%</MenuItem>
                            <MenuItem value={21}>21%</MenuItem>
                          </Select>
                        </FormControl>
                      </Paper>

                      {/* Total Summary - Update to show invoice-level VAT */}
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                          bgcolor: alpha(theme.palette.primary.main, 0.03)
                        }}
                      >
                        <Stack spacing={2}>
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            p: 1.5,
                            bgcolor: 'white',
                            borderRadius: '10px',
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
                          }}>
                            <Typography variant="subtitle1">{t("subtotal")}:</Typography>
                            <Typography variant="subtitle1" fontWeight="bold">
                              €{totalExcl.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            p: 1.5,
                            bgcolor: 'white',
                            borderRadius: '10px',
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
                          }}>
                            <Typography variant="subtitle1">{t("vat")} ({invoiceVAT}%):</Typography>
                            <Typography variant="subtitle1" fontWeight="bold">
                              €{totalVAT.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            p: 1.5,
                            bgcolor: 'white',
                            borderRadius: '10px',
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
                          }}>
                            <Typography variant="subtitle1" color="success.main">{t("discount")}:</Typography>
                            <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                              -€{discountAmount.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            p: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            borderRadius: '10px',
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                              {t("grand_total")}:
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                              €{grandTotal.toFixed(2)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>

                      {/* ✅ Peppol Toggle Section - Add this entire block */}
                      {selectedCustomer?.CustomerPeppolId && (
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            borderRadius: '12px',
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            bgcolor: alpha(theme.palette.success.main, 0.05)
                          }}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{
                                bgcolor: alpha(theme.palette.success.main, 0.15),
                                p: 1,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <SendIcon color="success" fontSize="small" />
                              </Box>
                              <Box>
                                <Typography variant="body2" fontWeight="bold" color="success.dark">
                                  {t("send_invoice_on_peppol")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Peppol ID: {selectedCustomer.CustomerPeppolId}
                                </Typography>
                              </Box>
                            </Box>
                            <Switch
                              checked={isSendOnPeppol}
                              onChange={(e) => setIsSendOnPeppol(e.target.checked)}
                              color="success"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: theme.palette.success.main,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: theme.palette.success.main,
                                }
                              }}
                            />
                          </Stack>
                        </Paper>
                      )}

                      {/* Action Buttons - this should already exist below */}
                      <Box>
                        <Grid container spacing={2} direction="column" alignItems="stretch">
                          {invoiceProducts.length === 0 && (
                            <Grid item xs={12} mb={1}>
                              <Box sx={{
                                p: 2,
                                borderRadius: '10px',
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Typography
                                  variant="body1"
                                  color="error"
                                  textAlign="center"
                                  fontWeight="medium"
                                >
                                  {t("please_add_at_least_one_product")}
                                </Typography>
                              </Box>
                            </Grid>
                          )}

                          {/* Preview Invoice button on top */}
                          <Grid item xs={12}>
                            <Button
                              variant="outlined"
                              fullWidth
                              size="large"
                              startIcon={<PreviewIcon />}
                              onClick={async () => {
                                try {
                                  const tempDiv = document.createElement("div");
                                  tempDiv.innerHTML = getFilledTemplate();
                                  
                                  // Style for proper rendering
                                  tempDiv.style.position = "absolute";
                                  tempDiv.style.left = "-9999px";
                                  tempDiv.style.width = "794px"; // A4 width in pixels at 96dpi
                                  tempDiv.style.background = "white";
                                  document.body.appendChild(tempDiv);

                                  // Wait for images to load
                                  const images = tempDiv.getElementsByTagName('img');
                                  await Promise.all(
                                    Array.from(images).map(img => {
                                      if (img.complete) return Promise.resolve();
                                      return new Promise(resolve => {
                                        img.onload = resolve;
                                        img.onerror = resolve;
                                      });
                                    })
                                  );

                                  // Generate canvas
                                  const canvas = await html2canvas(tempDiv, {
                                    scale: 2,
                                    useCORS: true,
                                    logging: false,
                                    width: 794,
                                    windowWidth: 794,
                                    backgroundColor: '#ffffff'
                                  });

                                  document.body.removeChild(tempDiv);

                                  // Create PDF
                                  const imgData = canvas.toDataURL('image/png');
                                  const pdf = new jsPDF({
                                    orientation: 'portrait',
                                    unit: 'mm',
                                    format: 'a4'
                                  });

                                  const pdfWidth = pdf.internal.pageSize.getWidth();
                                  const pdfHeight = pdf.internal.pageSize.getHeight();
                                  
                                  // Calculate image dimensions to fit A4
                                  const imgWidth = pdfWidth;
                                  const imgHeight = (canvas.height * pdfWidth) / canvas.width;

                                  // If content is taller than one page, scale it down
                                  if (imgHeight > pdfHeight) {
                                    const scaledWidth = (canvas.width * pdfHeight) / canvas.height;
                                    const scaledHeight = pdfHeight;
                                    pdf.addImage(imgData, 'PNG', 0, 0, scaledWidth, scaledHeight);
                                  } else {
                                    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
                                  }

                                  // Open in new tab
                                  const pdfBlob = pdf.output('blob');
                                  const pdfUrl = URL.createObjectURL(pdfBlob);
                                  window.open(pdfUrl, '_blank');
                                } catch (error) {
                                  console.error('PDF generation error:', error);
                                  toast.error(t("failed_to_generate_pdf"));
                                }
                              }}
                              disabled={invoiceProducts.length === 0}
                              sx={{
                                py: 1.5,
                                borderRadius: '12px',
                                borderWidth: '2px',
                                borderColor: theme.palette.primary.main,
                                fontSize: '1rem',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                mb: 2,
                                '&:hover': {
                                  borderWidth: '2px',
                                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                }
                              }}
                            >
                              {t("preview_invoice")}
                            </Button>
                          </Grid>

                          {/* Generate PDF button below */}
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              fullWidth
                              size="large"
                              startIcon={<PictureAsPdfIcon />}
                              onClick={handleAddInvoiceAndGeneratePDF}
                              disabled={invoiceProducts.length === 0}
                              sx={{
                                py: 1.5,
                                borderRadius: '12px',
                                background: "linear-gradient(45deg, #3b82f6 20%, #6366f1 90%)",
                                boxShadow: '0 8px 20px rgba(99, 102, 241, 0.3)',
                                fontSize: '1rem',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                transition: 'all 0.3s',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 10px 25px rgba(99, 102, 241, 0.4)'
                                }
                              }}
                            >
                              {t("generate_pdf")}
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>

      <InvoicePreviewDialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        html={previewHtml}
      />
    </Box>
  );
}

function generateInvoiceNumber(lastNumber = 1) {
  const year = new Date().getFullYear();
  return `INV-${year}-${String(lastNumber).padStart(4, "0")}`;
}

export default AddInvoicePage;
